import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "components/card";
import {
  MdArrowBack,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdEdit,
  MdSave,
} from "react-icons/md";
import { questionnaireService } from "services/questionnaireService";
import { fetchWithAuth } from "services/api";
import {
  Questionnaire,
  QuestionnaireSection,
  QuestionnaireQuestion,
} from "services/questionnaireService";

interface QuestionnairePrompt {
  id: number;
  questionnaire_id: number;
  prompt: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const QuestionnaireDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set()
  );

  // New state for prompt management
  const [prompt, setPrompt] = useState<QuestionnairePrompt | null>(null);
  const [promptLoading, setPromptLoading] = useState<boolean>(true);
  const [promptError, setPromptError] = useState<string | null>(null);
  const [isEditingPrompt, setIsEditingPrompt] = useState<boolean>(false);
  const [editedPrompt, setEditedPrompt] = useState<string>("");
  const [savingPrompt, setSavingPrompt] = useState<boolean>(false);

  useEffect(() => {
    fetchQuestionnaire();
    fetchPrompt();
  }, [id]);

  const fetchQuestionnaire = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await questionnaireService.getQuestionnaire(
        parseInt(id)
      );
      if (response.success) {
        setQuestionnaire(response.data);
        // Expand the first section by default
        if (response.data.structure?.sections?.length > 0) {
          setExpandedSections(
            new Set([response.data.structure.sections[0].id])
          );
        }
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError("Failed to fetch questionnaire details");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrompt = async () => {
    if (!id) return;

    try {
      setPromptLoading(true);
      const response = await fetchWithAuth(`/questionnaire-prompts/${id}`);

      if (response.success) {
        setPrompt(response.data);
        setEditedPrompt(response.data.prompt);
      } else {
        setPromptError(response.message);
      }
    } catch (err) {
      setPromptError("Error al cargar el prompt del cuestionario");
      console.error(err);
    } finally {
      setPromptLoading(false);
    }
  };

  const handleSavePrompt = async () => {
    if (!id) return;

    try {
      setSavingPrompt(true);
      const response = await fetchWithAuth(`/questionnaire-prompts/${id}`, {
        method: "PUT",
        body: JSON.stringify({
          prompt: editedPrompt,
          is_active: true,
        }),
      });

      if (response.success) {
        setPrompt(response.data);
        setIsEditingPrompt(false);
      } else {
        setPromptError(response.message);
      }
    } catch (err) {
      setPromptError("Error al guardar el prompt");
      console.error(err);
    } finally {
      setSavingPrompt(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const handleBack = () => {
    navigate("/admin/questionnaires");
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const renderQuestionOptions = (question: QuestionnaireQuestion) => {
    if (!question.options || question.options.length === 0) {
      return null;
    }

    return (
      <div className="mt-2 space-y-1">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Opciones:
        </p>
        <div className="ml-3 space-y-1">
          {question.options.map((option, index) => (
            <div
              key={index}
              className="text-sm text-gray-600 dark:text-gray-400"
            >
              <span className="font-medium">{option.label}</span>
              {option.score !== undefined && (
                <span className="ml-2 text-xs text-gray-500">
                  (Puntaje: {option.score})
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="border-t-transparent h-12 w-12 animate-spin rounded-full border-4 border-brand-500"></div>
      </div>
    );
  }

  if (error || !questionnaire) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-lg font-semibold text-red-500">
          {error || "Questionnaire not found"}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 grid h-full gap-5">
      {/* Header with back button and questionnaire name */}
      <div className="flex items-center">
        <button
          onClick={handleBack}
          className="mr-4 flex items-center justify-center rounded-lg p-2 text-navy-700 transition-all hover:bg-gray-100 dark:text-white dark:hover:bg-navy-700"
        >
          <MdArrowBack className="h-6 w-6" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            {questionnaire.name}
          </h1>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-brand-500 px-2 py-0.5 text-xs font-medium text-white">
              {questionnaire.code}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                questionnaire.is_active
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {questionnaire.is_active ? "Activo" : "Inactivo"}
            </span>
          </div>
        </div>
      </div>

      {/* Questionnaire Information Card */}
      <Card extra="w-full p-6">
        <h2 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
          Información General
        </h2>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-500">Descripción</p>
              <p className="text-base text-navy-700 dark:text-white">
                {questionnaire.description || "Sin descripción"}
              </p>
            </div>
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-500">
                Tipo de Evaluación
              </p>
              <p className="text-base text-navy-700 dark:text-white">
                {questionnaire.scoring_type}
              </p>
            </div>
          </div>
          <div>
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-500">
                Fecha de Creación
              </p>
              <p className="text-base text-navy-700 dark:text-white">
                {formatDate(questionnaire.created_at)}
              </p>
            </div>
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-500">
                Última Actualización
              </p>
              <p className="text-base text-navy-700 dark:text-white">
                {formatDate(questionnaire.updated_at)}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* New Prompt Card */}
      <Card extra="w-full p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-navy-700 dark:text-white">
            Prompt del Cuestionario
          </h2>
          {!isEditingPrompt ? (
            <button
              onClick={() => setIsEditingPrompt(true)}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
            >
              <MdEdit className="h-5 w-5" />
              <span>Editar Prompt</span>
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setIsEditingPrompt(false);
                  setEditedPrompt(prompt?.prompt || "");
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleSavePrompt}
                disabled={savingPrompt}
                className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {savingPrompt ? (
                  <>
                    <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-white"></div>
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <MdSave className="h-5 w-5" />
                    <span>Guardar Cambios</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {promptLoading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-2 border-brand-500"></div>
          </div>
        ) : promptError ? (
          <div className="rounded-lg bg-red-100 p-4 text-red-700">
            {promptError}
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-900">
            {isEditingPrompt ? (
              <textarea
                value={editedPrompt}
                onChange={(e) => setEditedPrompt(e.target.value)}
                rows={10}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-navy-700 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-500"
                placeholder="Ingrese el prompt del cuestionario..."
              />
            ) : (
              <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                {prompt?.prompt ||
                  "No hay prompt definido para este cuestionario."}
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Questionnaire Structure Card */}
      <Card extra="w-full p-6">
        <h2 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
          Estructura del Cuestionario
        </h2>

        {questionnaire.structure?.sections?.length ? (
          <div className="divide-y divide-gray-200 dark:divide-navy-700">
            {questionnaire.structure.sections.map(
              (section: QuestionnaireSection) => {
                const isExpanded = expandedSections.has(section.id);

                return (
                  <div key={section.id} className="py-3">
                    <div
                      className="flex cursor-pointer items-center justify-between"
                      onClick={() => toggleSection(section.id)}
                    >
                      <div className="flex items-center">
                        {isExpanded ? (
                          <MdKeyboardArrowUp className="mr-2 h-6 w-6 text-gray-500" />
                        ) : (
                          <MdKeyboardArrowDown className="mr-2 h-6 w-6 text-gray-500" />
                        )}
                        <div>
                          <p className="font-medium text-navy-700 dark:text-white">
                            {section.title}
                          </p>
                          <p className="text-sm text-gray-600">
                            {section.questions.length} pregunta(s)
                          </p>
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-3 rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Descripción:
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {section.description || "Sin descripción"}
                          </p>
                        </div>

                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Tipo de Respuesta:
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {section.response_type}
                          </p>
                        </div>

                        {section.options && section.options.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Opciones de Respuesta:
                            </p>
                            <div className="ml-3 mt-1 space-y-1">
                              {section.options.map((option, index) => (
                                <div
                                  key={index}
                                  className="text-sm text-gray-600 dark:text-gray-400"
                                >
                                  <span className="font-medium">
                                    {option.label}
                                  </span>
                                  {option.score !== undefined && (
                                    <span className="ml-2 text-xs text-gray-500">
                                      (Puntaje: {option.score})
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                          Preguntas:
                        </p>
                        <div className="space-y-3">
                          {section.questions
                            .sort((a, b) => a.order - b.order)
                            .map((question) => (
                              <div
                                key={question.id}
                                className="rounded-lg bg-white p-3 shadow-sm dark:bg-navy-700"
                              >
                                <div className="flex items-start">
                                  <span className="mr-2 text-sm font-medium text-gray-500">
                                    {question.order}.
                                  </span>
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-navy-700 dark:text-white">
                                      {question.text}
                                    </p>
                                    {renderQuestionOptions(question)}
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
            )}
          </div>
        ) : (
          <p className="text-center text-gray-500">
            Este cuestionario no tiene secciones definidas.
          </p>
        )}
      </Card>
    </div>
  );
};

export default QuestionnaireDetail;
